import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Counter = ({ target, suffix = "", prefix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      setCount(Math.floor(progress * target));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isInView, target, duration]);

  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

export const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleScrollTop = () => {
    window.scrollTo(0, 0);
  };

  return (
    <section
      data-testid="cta-section"
      ref={ref}
      className="py-16 md:py-24 lg:py-32 bg-[#1F2A44] relative overflow-hidden"
    >
      {/* Subtle accent */}
      <div className="absolute top-0 right-0 w-64 lg:w-96 h-64 lg:h-96 bg-[#D97904]/5 rounded-full blur-3xl" />
      
      <div className="container-peak relative z-10">
        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row justify-center items-center gap-8 sm:gap-12 md:gap-24 mb-12 lg:mb-16"
        >
          <div className="text-center">
            <div className="text-4xl sm:text-5xl lg:text-6xl text-[#D97904] mb-2" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
              <Counter target={1000} suffix="+" />
            </div>
            <p className="text-[#F4F1EC]/70 text-xs lg:text-sm font-['Syne'] uppercase tracking-wider">
              Keynotes Delivered
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl sm:text-5xl lg:text-6xl text-[#D97904] mb-2" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
              <Counter target={50000} suffix="+" />
            </div>
            <p className="text-[#F4F1EC]/70 text-xs lg:text-sm font-['Syne'] uppercase tracking-wider">
              Lives Impacted
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center max-w-2xl mx-auto px-4"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light leading-[1.15] mb-4 lg:mb-6 text-[#F4F1EC]">
            Ready to unlock your team's{' '}
            <em className="not-italic text-[#D97904]">true potential</em>?
          </h2>
          
          <p className="text-[#F4F1EC]/70 text-sm lg:text-lg mb-8 lg:mb-10">
            Let's discuss how we can help your organization achieve sustainable peak performance.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/keynotes"
              onClick={handleScrollTop}
              data-testid="cta-explore-btn"
              className="btn-primary inline-flex items-center justify-center gap-3 whitespace-nowrap"
            >
              Explore Our Programs
              <ArrowRight size={16} />
            </Link>
            <a
              href="https://shayamal.peak9.in/courses/performance-by-design?communityLink=shayamal&courseSlug=performance-by-design"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-enroll-course-btn"
              className="inline-flex items-center justify-center gap-3 font-['Syne'] text-xs uppercase tracking-[0.12em] text-[#F4F1EC] border border-[#F4F1EC]/30 px-8 py-4 hover:bg-[#F4F1EC]/10 hover:border-[#F4F1EC]/60 transition-all whitespace-nowrap"
            >
              Enroll in Performance by Design
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
